import type { ComprobacionFace } from '@shared/certificados';

/** `fetchWithAuth` de `UserContext`: añade el token y el prefijo `/api`. */
export type FetchConAuth = (url: string, options?: RequestInit) => Promise<Response>;

type Resultado = { ok: true } | { ok: false; error: string };

async function leerJson(respuesta: Response): Promise<Record<string, any>> {
  try {
    const cuerpo: unknown = await respuesta.json();
    // `null`, un texto o una lista no son un cuerpo de error utilizable
    return typeof cuerpo === 'object' && cuerpo !== null && !Array.isArray(cuerpo) ? cuerpo : {};
  } catch {
    return {};
  }
}

/**
 * Texto de una respuesta de error: las rutas responden `{error}` y los
 * middlewares (auth, límite, validación) `{message}`.
 */
function textoDeError(cuerpo: Record<string, any>, porDefecto: string): string {
  return cuerpo.error ?? cuerpo.message ?? porDefecto;
}

export type ResultadoSubida =
  | { ok: true; expiresIn: string }
  | { ok: false; codigo?: string; error: string };

/** POST /certificados/upload-p12. Nunca lanza: un fallo vuelve como `{ ok: false }`. */
export async function subirP12Api(
  fetchWithAuth: FetchConAuth,
  datos: { nombre: string; p12Data: string; password: string; confirmarSinNif?: boolean }
): Promise<ResultadoSubida> {
  try {
    const respuesta = await fetchWithAuth('/certificados/upload-p12', {
      method: 'POST',
      body: JSON.stringify(datos),
    });
    const cuerpo = await leerJson(respuesta);
    // Un 2xx sin JSON legible: el certificado ya se subió; la lista mostrará su estado real.
    if (respuesta.ok) return { ok: true, expiresIn: cuerpo.expiresIn ?? '' };
    return {
      ok: false,
      ...(cuerpo.codigo !== undefined ? { codigo: cuerpo.codigo } : {}),
      error: textoDeError(cuerpo, 'No se pudo subir el certificado.'),
    };
  } catch (error) {
    console.error('Error al subir el certificado:', error);
    return { ok: false, error: 'No se pudo subir el certificado.' };
  }
}

export interface CuerpoActivacion {
  sinComprobar?: true;
  confirmacion?: 'ACTIVAR_SIN_COMPROBAR' | 'ACTIVAR_SIN_VERIFICAR';
}

export type RespuestaActivacion =
  | { ok: true; comprobacion?: ComprobacionFace }
  | { ok: false; codigo?: string; error: string; comprobacion?: ComprobacionFace };

/** POST /certificados/:id/activar. Los rechazos de FACe vuelven con su `codigo`. */
export async function activarCertificadoApi(
  fetchWithAuth: FetchConAuth,
  id: string,
  cuerpo: CuerpoActivacion = {}
): Promise<RespuestaActivacion> {
  try {
    const respuesta = await fetchWithAuth(`/certificados/${id}/activar`, {
      method: 'POST',
      body: JSON.stringify(cuerpo),
    });
    const datos = await leerJson(respuesta);
    if (respuesta.ok) return { ok: true, ...(datos.comprobacion !== undefined ? { comprobacion: datos.comprobacion } : {}) };
    return {
      ok: false,
      ...(datos.codigo !== undefined ? { codigo: datos.codigo } : {}),
      error: textoDeError(datos, 'No se pudo activar el certificado.'),
      ...(datos.comprobacion !== undefined ? { comprobacion: datos.comprobacion } : {}),
    };
  } catch (error) {
    console.error('Error al activar el certificado:', error);
    return { ok: false, error: 'No se pudo activar el certificado.' };
  }
}

/** POST /certificados/:id/tareas */
export async function marcarTareaApi(
  fetchWithAuth: FetchConAuth,
  id: string,
  tarea: 'bajaFace' | 'revocacionFnmt'
): Promise<Resultado> {
  try {
    const respuesta = await fetchWithAuth(`/certificados/${id}/tareas`, {
      method: 'POST',
      body: JSON.stringify({ tarea }),
    });
    if (respuesta.ok) return { ok: true };
    return { ok: false, error: textoDeError(await leerJson(respuesta), 'No se pudo marcar la tarea.') };
  } catch (error) {
    console.error('Error al marcar la tarea:', error);
    return { ok: false, error: 'No se pudo marcar la tarea.' };
  }
}

/** DELETE /certificados/:id */
export async function eliminarCertificadoApi(
  fetchWithAuth: FetchConAuth,
  id: string
): Promise<Resultado> {
  try {
    const respuesta = await fetchWithAuth(`/certificados/${id}`, { method: 'DELETE' });
    if (respuesta.ok) return { ok: true };
    return { ok: false, error: textoDeError(await leerJson(respuesta), 'No se pudo eliminar el certificado.') };
  } catch (error) {
    console.error('Error al eliminar el certificado:', error);
    return { ok: false, error: 'No se pudo eliminar el certificado.' };
  }
}

/** Nombre de fichero seguro: minúsculas, sin acentos ni símbolos. */
function nombreDeFichero(nombre: string): string {
  const base = nombre
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${base || 'certificado'}.pem`;
}

/**
 * Descarga la parte pública (.pem) para darla de alta en FACe. Lanza un
 * `Error` con el texto del servidor si no se puede.
 */
export async function descargarPartePublica(
  fetchWithAuth: FetchConAuth,
  certificado: { id: string; nombre: string }
): Promise<void> {
  const respuesta = await fetchWithAuth(`/certificados/${certificado.id}/publico.pem`);
  if (!respuesta.ok) {
    throw new Error(textoDeError(await leerJson(respuesta), 'No se pudo descargar el certificado.'));
  }
  const url = URL.createObjectURL(await respuesta.blob());
  const enlace = document.createElement('a');
  try {
    enlace.href = url;
    enlace.download = nombreDeFichero(certificado.nombre);
    document.body.appendChild(enlace);
    enlace.click();
  } finally {
    enlace.remove();
    // Safari necesita que la descarga arranque antes de revocar la URL.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
